import { memo, useMemo } from "react";
import { styleWithVars } from "@lib/cssVars";
import { buildAxisTicks } from "./densityAxis.lib";
import type {
    DensityChartBucket,
    DensityChartBucketUnit,
} from "./densityBuckets.lib";
import { BAR_PITCH_PX } from "./densityViewport.lib";
import styles from "./DensityChartAxis.module.scss";

interface DensityChartAxisProps {
    buckets: DensityChartBucket[];
    unit: DensityChartBucketUnit;
    /** Today, for the slot after its bar. */
    endDate: string;
    /** Where the view starts, in bars from the first bucket's left. */
    visibleStart: number;
}

/** The baseline under the bars, with ticks and labels centered on their bars. */
export const DensityChartAxis = memo(function DensityChartAxis({
    buckets,
    unit,
    endDate,
    visibleStart,
}: DensityChartAxisProps) {
    const ticks = useMemo(
        () =>
            buildAxisTicks({
                buckets,
                unit,
                barWidth: BAR_PITCH_PX,
                endDate,
                visibleStart,
            }),
        [buckets, unit, endDate, visibleStart],
    );
    return (
        <div className={styles.axis} aria-hidden>
            {ticks.map(({ index, kind, label, yearLabel }) => (
                <div
                    key={index}
                    className={styles.tick}
                    data-kind={kind}
                    data-tick-start={buckets[index]?.start}
                    data-tick-end={buckets[index]?.end}
                    style={styleWithVars({
                        "--tick-left": `${((index + 0.5) / buckets.length) * 100}%`,
                    })}
                >
                    {label && (
                        <span className={styles.label} data-axis-label>
                            <span>{label}</span>
                            {yearLabel && <span>{yearLabel}</span>}
                        </span>
                    )}
                </div>
            ))}
        </div>
    );
});
